import axios from 'axios'
import { getClientVisibleIPv4ForAudit } from './clientVisibleIp'
import { clearResponseData, unAuthorizedError } from './interceptors'
import type { CustomAxiosResponse } from './types'

export const useHttp = () => {
  const baseUrl = import.meta.env.VITE_BASE_URL

  const http = axios.create({
    baseURL: baseUrl,
  })

  http.defaults.headers.common['Content-Type'] = 'application/json'
  http.defaults.headers.common['Accept'] = 'application/json, text/plain, */*'

  http.interceptors.request.use(async (config) => {
    config.headers = config.headers ?? {}
    const headers = config.headers as Record<string, string>

    /**
     * Токен читаем на каждый запрос, а не при создании клиента: клиент может
     * быть создан до логина (например, стор global_search живёт всю сессию),
     * иначе он навсегда застревает с `Bearer null`.
     */
    const token = localStorage.getItem('token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    } else {
      delete headers['Authorization']
    }

    const ip = await getClientVisibleIPv4ForAudit()
    if (ip) {
      headers['X-Client-Visible-IP'] = ip
    }
    return config
  })

  const setAuthorization = (token: string) => {
    http.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  /**
   * Функция задания базового url
   * @param url
   */
  const setBaseUrl = (url: string) => {
    http.defaults.baseURL = url
  }

  /**
   * Функция отправки GET запросов
   * @param url
   * @param query
   * @param options
   */
  const get = (url: string, query?: any, options?: any): Promise<CustomAxiosResponse> => {
    return http.get(url, { ...options, params: query })
  }

  /**
   * Функция отправки POST запросов
   * @param url
   * @param data
   * @param options
   */
  const post = (url: string, data: any, options?: any): Promise<CustomAxiosResponse> => {
    return http.post(url, data, { ...options })
  }

  /**
   * Функция отправки DELETE запросов
   * @param url
   * @param data
   */
  const del = (url: string, data?: any): Promise<CustomAxiosResponse> => {
    return http.delete(url, {
      data,
    })
  }

  /**
   * Функция отправки PUT запросов
   * @param url
   * @param data
   */
  const put = (url: string, data: any): Promise<CustomAxiosResponse> => {
    return http.put(url, data)
  }

  /**
   * Функция отправки PATCH запросов
   * @param url
   * @param data
   */
  const patch = (url: string, data: any): Promise<CustomAxiosResponse> => {
    return http.patch(url, data)
  }

  /**
   * Функция отправки POST файлов на сервер
   * @param url
   * @param data
   */
  const file = (url: string, data: any): Promise<CustomAxiosResponse> => {
    return http.post(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  /* Обработчик общих ответов */
  http.interceptors.response.use(clearResponseData, unAuthorizedError)

  return {
    setAuthorization,
    setBaseUrl,
    get,
    post,
    put,
    patch,
    del,
    file,
  }
}
